import React, { useEffect, useMemo, useState } from "react";
import "./UsersManager.css";

const API_URL = "https://cms.amservices.eu/api/hrtp_users_manager.php";
const EMPTY_FORM = { id: null, username: "", password: "", role: "user", planning_team_key: "" };
const ROLES = {
  user: { label: "Utilisateur · son équipe uniquement", icon: "🟢" },
  admin: { label: "Administrateur · accès complet", icon: "🔴" },
};

function token() {
  const user = JSON.parse(localStorage.getItem("am_user") || "null");
  return localStorage.getItem("am_token") || user?.token || "";
}

function formatDate(value) {
  if (!value) return "Jamais";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
}

export default function HrtpUsersManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => [user.id, user.username, user.role, user.planning_team_key]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [users, search]);

  const request = async (payload = null) => {
    const authToken = token();
    const response = await fetch(API_URL, {
      method: payload ? "POST" : "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Auth-Token": authToken,
      },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    const text = await response.text();
    let data;
    try { data = text ? JSON.parse(text) : {}; }
    catch { throw new Error("Réponse invalide du serveur."); }
    if (!response.ok || !data.success) throw new Error(data.message || `Erreur HTTP ${response.status}`);
    return data;
  };

  const loadUsers = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const data = await request();
      setUsers(Array.isArray(data.users) ? data.users : []);
      setTeams(Array.isArray(data.teams) ? data.teams : []);
    } catch (err) {
      setError(err.message || "Impossible de charger les utilisateurs HRTP.");
    } finally { if (showLoader) setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (user) => {
    setForm({
      id: Number(user.id), username: user.username || "", password: "",
      role: ROLES[user.role] ? user.role : "user",
      planning_team_key: user.planning_team_key || "",
    });
    setError("");
    setMessage("");
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await request({
        action: form.id ? "update" : "create", id: form.id,
        username: form.username.trim(), password: form.password, role: form.role,
        planning_team_key: form.planning_team_key || null,
      });
      setMessage(data.message || "Utilisateur enregistré.");
      setForm(EMPTY_FORM);
      await loadUsers(false);
    } catch (err) {
      setError(err.message || "L’enregistrement a échoué.");
    } finally { setSaving(false); }
  };

  const remove = async (user) => {
    if (!window.confirm(`Supprimer le compte HRTP ${user.username} ?`)) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await request({ action: "delete", id: Number(user.id) });
      setMessage(data.message || "Utilisateur supprimé.");
      if (Number(form.id) === Number(user.id)) setForm(EMPTY_FORM);
      await loadUsers(false);
    } catch (err) {
      setError(err.message || "La suppression a échoué.");
    } finally { setSaving(false); }
  };

  const teamName = (key) => teams.find((team) => team.team_key === key)?.name || "Aucune équipe";
  const editingAdmin = String(users.find((user) => Number(user.id) === Number(form.id))?.username || "").toLowerCase() === "admin";

  return (
    <main className="users-manager-page users-manager-hrtp">
      <header className="users-manager-header">
        <div className="users-manager-title">
          <span className="um-eyebrow">Administration · HRTP</span>
          <h1>Utilisateurs HRTP</h1>
          <p>Le compte donne accès au CRM, au Kanban et au planning de l’équipe choisie.</p>
        </div>
        <div className="um-current-admin"><span>Administrateur CMS connecté</span><strong>{currentUser?.nom || currentUser?.login}</strong></div>
      </header>

      <section className="um-summary" aria-label="Résumé HRTP">
        <article><strong>{users.length}</strong><span>comptes HRTP</span></article>
        <article><strong>{users.filter((user) => user.role === "admin").length}</strong><span>administrateur(s)</span></article>
        <article><strong>{users.filter((user) => user.planning_team_key).length}</strong><span>rattachés à une équipe</span></article>
      </section>

      {message && <div className="um-alert um-alert-success">{message}</div>}
      {error && <div className="um-alert um-alert-error">{error}</div>}

      <div className="um-content-grid">
        <section className="um-panel um-form-panel">
          <div className="um-panel-title">
            <div><span className="um-step">{form.id ? `Compte #${form.id}` : "Nouveau compte"}</span><h2>{form.id ? "Modifier l’utilisateur" : "Ajouter un utilisateur HRTP"}</h2></div>
            {form.id && <button className="um-link-button" type="button" onClick={() => setForm(EMPTY_FORM)}>Annuler</button>}
          </div>
          {editingAdmin && <div className="um-master-notice">Le compte administrateur HRTP est protégé.</div>}
          <form onSubmit={submit}>
            <div className="um-form-group"><label htmlFor="hrtp-username">Identifiant</label><input id="hrtp-username" className="um-input" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} disabled={editingAdmin} required /></div>
            <div className="um-form-group"><label htmlFor="hrtp-password">{form.id ? "Nouveau mot de passe" : "Mot de passe initial"}</label><input id="hrtp-password" className="um-input" type="password" minLength={8} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder={form.id ? "Vide = mot de passe inchangé" : "8 caractères minimum"} required={!form.id} autoComplete="new-password" /></div>
            <div className="um-form-group"><label htmlFor="hrtp-role">Rôle</label><select id="hrtp-role" className="um-select" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} disabled={editingAdmin}>{Object.entries(ROLES).map(([value, role]) => <option key={value} value={value}>{role.icon} {role.label}</option>)}</select></div>
            <div className="um-form-group"><label htmlFor="hrtp-team">Équipe autorisée</label><select id="hrtp-team" className="um-select" value={form.planning_team_key} onChange={(event) => setForm({ ...form, planning_team_key: event.target.value })}><option value="">Aucune équipe</option>{teams.map((team) => <option key={team.team_key} value={team.team_key}>{team.name}</option>)}</select><span className="um-hint">Un utilisateur standard ne verra que le planning et les projets Kanban de cette équipe.</span></div>
            <button className="um-btn um-btn-primary" type="submit" disabled={saving}>{saving ? "Enregistrement…" : form.id ? "Enregistrer les modifications" : "Créer le compte"}</button>
          </form>
        </section>

        <section className="um-panel um-list-panel">
          <div className="um-toolbar"><div><span className="um-step">Annuaire</span><h2>Équipe HRTP</h2></div><input className="um-input um-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher…" /></div>
          {loading ? <div className="um-loading">Chargement des comptes…</div> : <div className="um-table-wrap"><table className="um-table"><thead><tr><th>Utilisateur</th><th>Rôle</th><th>Équipe autorisée</th><th>Dernière connexion</th><th>Actions</th></tr></thead><tbody>
            {filteredUsers.map((user) => <tr key={user.id}><td><div className="um-user-cell"><span className="um-avatar">{String(user.username).charAt(0).toUpperCase()}</span><span><strong>{user.username}</strong><small>#{user.id}</small></span></div></td><td><span className={`um-role um-role-${user.role}`}>{ROLES[user.role]?.icon} {ROLES[user.role]?.label || user.role}</span></td><td>{teamName(user.planning_team_key)}</td><td className="um-date">{formatDate(user.last_login)}</td><td><div className="um-row-actions"><button className="um-btn um-btn-warning" type="button" onClick={() => edit(user)} disabled={saving}>Modifier</button>{String(user.username).toLowerCase() !== "admin" && <button className="um-btn um-btn-danger" type="button" onClick={() => remove(user)} disabled={saving}>Supprimer</button>}</div></td></tr>)}
            {!filteredUsers.length && <tr><td colSpan="5" className="um-empty">Aucun utilisateur HRTP trouvé.</td></tr>}
          </tbody></table></div>}
        </section>
      </div>
    </main>
  );
}
